/*
 * Sensory feedback for habit check-ins: a short success chime, a longer
 * milestone fanfare, haptics, and a confetti burst. Everything here is
 * best-effort — on platforms without Web Audio / vibration / element.animate
 * (including jsdom in tests) each call is a silent no-op.
 */

import { computeStreak, isMilestone } from './streaks';
import { TOAST_TIMEOUT } from '../constants';

const MUTE_KEY = 'habits.soundMuted';

function isSoundMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    return false;
  }
}

function setSoundMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, String(muted));
  } catch {
    // localStorage unavailable — mute state just won't persist
  }
}

let audioCtx = null;

function getAudioContext() {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  // Browsers suspend contexts created before a user gesture; check-ins are
  // always user gestures, so resuming here is allowed.
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

// Notes may override the default oscillator type and volume per note, so a
// chime can mix a bright melody with a quieter low-end support layer.
function playNotes(notes, type, volume) {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    notes.forEach(({ freq, at, dur, type: noteType, volume: noteVolume }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = noteType || type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t0 + at);
      gain.gain.linearRampToValueAtTime(noteVolume ?? volume, t0 + at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + at + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0 + at);
      osc.stop(t0 + at + dur + 0.05);
    });
  } catch {
    // sound is decoration, never let it break a check-in
  }
}

// C-major pentatonic ladder (C5 D5 E5 G5 A5), extended upward by octaves.
// Pentatonic on purpose: every rung is consonant with every other, so the
// streak pitch can climb for days without ever landing on a sour interval —
// the same trick match-3 games use for escalating combo stingers.
const PENTATONIC = [523.25, 587.33, 659.25, 783.99, 880.0];

function ladder(step) {
  return PENTATONIC[step % PENTATONIC.length] * 2 ** Math.floor(step / PENTATONIC.length);
}

// The ladder tops out two octaves up so long streaks stay pleasant, not shrill.
const MAX_LADDER_STEP = 9;

/*
 * Check-in chime, scaled by the current streak. Success only — unchecking is
 * silent. A plain check-in is a soft two-note rising pluck (~230ms); each
 * consecutive streak day starts the pluck one rung higher, so the streak is
 * audible as a climbing combo. From 7 days the sound turns more aggressive —
 * an extra passing note plus a sawtooth low-end layer — and from 30 days it
 * gains an octave shimmer on top. Richness scales; loudness barely does.
 */
function playCheckChime(streakCount = 1) {
  const step = Math.min(Math.max(streakCount, 1) - 1, MAX_LADDER_STEP);
  const root = ladder(step);
  const top = ladder(step + 2);
  const notes = [
    { freq: root, at: 0, dur: 0.1 },
    { freq: top, at: 0.07, dur: 0.16 },
  ];
  if (streakCount >= 7) {
    notes.push({ freq: ladder(step + 1), at: 0.035, dur: 0.08, volume: 0.05 });
    notes.push({ freq: root / 2, at: 0, dur: 0.14, type: 'sawtooth', volume: 0.028 });
  }
  if (streakCount >= 30) {
    notes.push({ freq: top * 2, at: 0.12, dur: 0.18, type: 'triangle', volume: 0.05 });
  }
  playNotes(notes, 'sine', 0.085);
}

/*
 * Milestone fanfare, reserved for landmark streak days. Bigger milestones get
 * a longer rising run and a low-end weight note: 7 days is a four-note
 * arpeggio (~550ms), 30/100/365 each extend the run one note higher.
 */
function playMilestoneFanfare(streakDays = 7) {
  const tier = streakDays >= 365 ? 3 : streakDays >= 100 ? 2 : streakDays >= 30 ? 1 : 0;
  const run = [
    { freq: 523.25, at: 0, dur: 0.12 }, // C5
    { freq: 659.25, at: 0.09, dur: 0.12 }, // E5
    { freq: 783.99, at: 0.18, dur: 0.12 }, // G5
    { freq: 1046.5, at: 0.27, dur: 0.28 }, // C6
    ...[
      { freq: 1318.51, at: 0.36, dur: 0.28 }, // E6 — 30 days and up
      { freq: 1567.98, at: 0.45, dur: 0.32 }, // G6 — 100 days and up
      { freq: 2093.0, at: 0.54, dur: 0.4 }, // C7 — a full year
    ].slice(0, tier),
  ];
  if (tier > 0) {
    run.push({ freq: 261.63, at: 0, dur: 0.5, type: 'sawtooth', volume: 0.035 }); // C4 weight
  }
  playNotes(run, 'triangle', 0.1);
}

function vibrate(pattern = 15) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(pattern);
  }
}

const CONFETTI_COLORS = ['#319795', '#38A169', '#D69E2E', '#DD6B20', '#3182CE', '#D53F8C'];

// Dependency-free confetti burst using the Web Animations API.
function burstConfetti(pieceCount = 36) {
  if (typeof document === 'undefined' || !HTMLElement.prototype.animate) return;
  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.style.cssText =
    'position:fixed;inset:0;overflow:hidden;pointer-events:none;z-index:9999;';
  document.body.appendChild(container);

  const originX = window.innerWidth / 2;
  const originY = window.innerHeight * 0.35;
  for (let i = 0; i < pieceCount; i++) {
    const piece = document.createElement('div');
    const size = 6 + Math.random() * 6;
    piece.style.cssText =
      `position:absolute;left:${originX}px;top:${originY}px;` +
      `width:${size}px;height:${size * 0.6}px;` +
      `background:${CONFETTI_COLORS[i % CONFETTI_COLORS.length]};`;
    container.appendChild(piece);

    const angle = Math.random() * 2 * Math.PI;
    const distance = 80 + Math.random() * Math.min(260, window.innerWidth / 3);
    const dx = Math.cos(angle) * distance;
    const fall = 160 + Math.random() * 240;
    piece.animate(
      [
        { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
        {
          transform: `translate(${dx}px,${Math.sin(angle) * distance * 0.5 + fall}px) rotate(${540 - Math.random() * 1080}deg)`,
          opacity: 0,
        },
      ],
      {
        duration: 900 + Math.random() * 700,
        easing: 'cubic-bezier(0.15,0.6,0.35,1)',
        fill: 'forwards',
      }
    );
  }
  setTimeout(() => container.remove(), 1800);
}

const ENCOURAGEMENTS = [
  'Nice — showing up is the hard part.',
  'Another brick in the wall. 💪',
  'Done. Future you says thanks.',
  'Consistency looks good on you.',
  'That’s how habits are built.',
];

/*
 * Occasionally returns an encouraging message for an ordinary check-in,
 * otherwise null. The base feedback (animation/chime) always fires, so the
 * app never feels unresponsive — this only adds an occasional extra.
 */
function maybeEncouragement(chance = 0.12, rand = Math.random) {
  if (rand() >= chance) return null;
  return ENCOURAGEMENTS[Math.floor(rand() * ENCOURAGEMENTS.length)];
}

/*
 * The full check-in reward, shared by every surface that can log a habit
 * (the Habits list and the calendar on the habit card): haptic tick, then
 * either the milestone fanfare + confetti + streak toast on landmark days,
 * or the streak-scaled chime with an occasional encouragement toast for
 * same-day check-ins. `logs` must already include the new entry so the
 * streak is computed on the state the user just created.
 */
function celebrateCheckIn({ toast, prompt, logs, date }) {
  vibrate(15);
  const streak = computeStreak(logs);
  if (streak.status === 'active' && isMilestone(streak.count)) {
    playMilestoneFanfare(streak.count);
    burstConfetti();
    toast({
      title: `${streak.count}-day streak! 🔥`,
      description: `"${prompt}" — ${streak.count} days in a row.`,
      status: 'success',
      duration: TOAST_TIMEOUT,
      isClosable: true,
    });
    return;
  }
  // Streak height is audible: the chime climbs one rung per consecutive day.
  playCheckChime(streak.status === 'active' ? streak.count : 1);
  const isToday = new Date(date).toDateString() === new Date().toDateString();
  if (isToday) {
    const message = maybeEncouragement();
    if (message) {
      toast({ description: message, status: 'success', duration: 2000 });
    }
  }
}

export {
  isSoundMuted,
  setSoundMuted,
  playCheckChime,
  playMilestoneFanfare,
  vibrate,
  burstConfetti,
  maybeEncouragement,
  celebrateCheckIn,
  ENCOURAGEMENTS,
};
